import React, { useState } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import classNames from "classnames";
import { z } from "zod";
import { BusinessTaxReturns, Entity, FirmSize } from "@common/types/entity";
import { Button } from "@design-system/Button/Button";
import { GridCol } from "@design-system/Grid/GridCol";
import { GridRow } from "@design-system/Grid/GridRow";
import { Picker } from "@design-system/Picker/Picker.lib";
import { useUpdateUserMutation } from "@api/user.api";
import { useOrganization } from "@api/organization.api";
import { useUser } from "@hooks/useUser";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { EnterKeyHint } from "@components/onboarding/components/EnterKeyHint";
import { VStack } from "@design-system/VStack/VStack";
import {
    businessTaxReturnsLabels,
    firmSizeLabels,
} from "../../entity/constants";
import animations from "../layout/OnboardingLayout.module.scss";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import fieldSpacing from "../components/fieldSpacing.module.scss";
import { useUserDisplayNames } from "../useUserDisplayNames";
import commonStyles from "./common.module.scss";

export interface FirmInfoValues {
    businessName: string;
    firmSize: FirmSize;
    businessTaxReturns: BusinessTaxReturns;
}

interface FirmInfoProps {
    entity: Entity;
    initialValues: FirmInfoValues | null;
    back(): void;
    next(values: FirmInfoValues): void;
}

const sizes = Object.values(FirmSize);
const firmSizeItems = sizes.map((value) => ({
    value,
    label: firmSizeLabels[value],
}));

const businessTaxReturnsItems = Object.values(BusinessTaxReturns).map(
    (value) => ({ value, label: businessTaxReturnsLabels[value] }),
);

const firmInfoFormSchema = z.object({
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    businessName: z.string().min(1, "Firm name is required"),
    firmSize: z.nativeEnum(FirmSize, { required_error: "Select a size" }),
    businessTaxReturns: z.nativeEnum(BusinessTaxReturns, {
        required_error: "Select a number of tax returns",
    }),
    unverifiedPhoneNumber: z
        .string()
        .min(1, "Phone number is required")
        .regex(/^\d{10}$/, "Enter a valid 10-digit phone number"),
});

type FirmInfoFormSchema = z.infer<typeof firmInfoFormSchema>;

const fields = createFormikFields<FirmInfoFormSchema>();

export const FirmInfo: React.FC<FirmInfoProps> = ({
    entity,
    initialValues,
    back,
    next,
}) => {
    const [loading, setLoading] = useState(false);
    const { data: organization } = useOrganization();
    const user = useUser();
    const updateUserMutation = useUpdateUserMutation();
    const { firstName, lastName } = useUserDisplayNames(user, entity);

    const formik = useFormik<FirmInfoFormSchema>({
        initialValues: {
            firstName: firstName ?? "",
            lastName: lastName ?? "",
            businessName:
                initialValues?.businessName ??
                organization?.name ??
                entity.name ??
                "",
            firmSize:
                initialValues?.firmSize ??
                organization?.profile?.firmSize ??
                entity.profile?.firmSize ??
                sizes[0],
            businessTaxReturns:
                initialValues?.businessTaxReturns ??
                organization?.profile?.businessTaxReturns ??
                BusinessTaxReturns["0_249"],
            unverifiedPhoneNumber:
                user?.unverifiedPhoneNumber ??
                entity.user?.unverifiedPhoneNumber ??
                "",
        },
        validate: validateFormikWithZodSchema(firmInfoFormSchema),
        onSubmit: submitHelper({
            loading,
            setLoading,
            handler: async ({
                firstName: firstNameValue,
                lastName: lastNameValue,
                businessName,
                firmSize,
                businessTaxReturns,
                unverifiedPhoneNumber,
            }) => {
                await updateUserMutation.mutateAsync({
                    preferredName: `${firstNameValue} ${lastNameValue}`.trim(),
                    unverifiedPhoneNumber,
                });

                next({ businessName, firmSize, businessTaxReturns });
            },
        }),
    });

    return (
        <div
            className={classNames(commonStyles.content420, animations.fadeIn1)}
        >
            <header
                className={classNames(
                    commonStyles.titleContainer,
                    animations.fadeIn2,
                )}
            >
                <h1 className={commonStyles.title}>Get started with Kick</h1>
            </header>

            <FormikProvider value={formik}>
                <Form onSubmit={formik.handleSubmit}>
                    <GridRow gap="24px">
                        <GridCol xs={12} sm={6}>
                            <fields.Text
                                name="firstName"
                                label="First name"
                                size="xl"
                                className={fieldSpacing.inputField}
                            />
                        </GridCol>
                        <GridCol xs={12} sm={6}>
                            <fields.Text
                                name="lastName"
                                label="Last name"
                                size="xl"
                                className={fieldSpacing.inputField}
                            />
                        </GridCol>
                    </GridRow>

                    <fields.Phone
                        name="unverifiedPhoneNumber"
                        label="Phone number"
                        size="sm"
                        className={fieldSpacing.inputField}
                    />

                    <fields.Text
                        name="businessName"
                        label="Firm name"
                        size="xl"
                        className={fieldSpacing.inputField}
                    />

                    <GridRow gap="24px">
                        <GridCol xs={12} sm={6}>
                            <FormikField<FirmSize>
                                name="firmSize"
                                label="Bookkeeping clients"
                                control="button"
                                className={fieldSpacing.buttonField}
                            >
                                {({ helpers, field, invalid }) => (
                                    <Picker.Select
                                        items={firmSizeItems}
                                        value={field.value ?? null}
                                        onValueChange={(value) => {
                                            if (value) {
                                                helpers.setValue(value);
                                            }
                                        }}
                                        placeholder="Select"
                                        aria-invalid={invalid}
                                        trigger={
                                            <Picker.Button
                                                variant="primary"
                                                size="xl"
                                                block
                                            />
                                        }
                                    />
                                )}
                            </FormikField>
                        </GridCol>
                        <GridCol xs={12} sm={6}>
                            <FormikField<BusinessTaxReturns>
                                name="businessTaxReturns"
                                label="Tax returns"
                                control="button"
                                className={fieldSpacing.buttonField}
                            >
                                {({ helpers, field, invalid }) => (
                                    <Picker.Select
                                        items={businessTaxReturnsItems}
                                        value={field.value ?? null}
                                        onValueChange={(value) => {
                                            if (value) {
                                                helpers.setValue(value);
                                            }
                                        }}
                                        placeholder="Select"
                                        aria-invalid={invalid}
                                        trigger={
                                            <Picker.Button
                                                variant="primary"
                                                size="xl"
                                                block
                                            />
                                        }
                                    />
                                )}
                            </FormikField>
                        </GridCol>
                    </GridRow>

                    <VStack as="footer" align="center" className="mt-4">
                        <ButtonWithLoader
                            type="submit"
                            variant="primary"
                            loading={loading}
                            block
                            size="xl"
                            rightIcon={<EnterKeyHint />}
                        >
                            Next
                        </ButtonWithLoader>
                        <Button
                            variant="secondary"
                            disabled={loading}
                            type="button"
                            onClick={back}
                            className="mt-4"
                            size="xl"
                        >
                            Back
                        </Button>
                    </VStack>
                </Form>
            </FormikProvider>
        </div>
    );
};
